import PropTypes from 'prop-types';
import React, { useCallback } from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { tracker } from 'app-components/utils/core';
import CheckExampleGraphic from 'assets/images/transfers/CheckExampleGraphic.svg';
import CheckExampleGraphicDark from 'assets/images/transfers/CheckExampleGraphicDark.svg';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  checkbox: {
    marginBottom: 20, // This is so we align the checkbox to the start of the text
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  termsLink: {
    textDecorationLine: 'none',
    color: theme.palette.primary.main,
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover': {
      textDecorationLine: 'underline',
    },
  },
  grid: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]': {
      MozAppearance: 'none',
    },
    '&.margin': {
      marginBottom: 10,
    },
  },
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  textField: {
    marginRight: 20,
    width: '100%',
  },
  checkWrapper: {
    width: '80%',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginRight: 'auto',
    marginLeft: 'auto',
    alignSelf: 'center',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.info.light,
  },
  textRow: {
    marginTop: theme.spacing(1),
    alignItems: 'center',
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
  },
  linkItem: {
    marginTop: 3, // Align link to typography. Link seems to have different alignment
    alignItems: 'center',
  },
}));

const SelectAccountForm = ({
  account,
  accountNumber,
  routingNumber,
  onChange,
  fieldErrors,
  institution,
  agreedToTerms,
  toggleAgreedToTerms,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();

  const onChangeProxy = useCallback(
    (newSelected, newAccountNumber, newRoutingNumber) => {
      if (newSelected) {
        const newAccountNumberMaxed = newAccountNumber?.length > 17 ? newAccountNumber.slice(0, 17) : newAccountNumber;
        onChange({
          id: account?.id,
          accountNumber: newAccountNumberMaxed,
          routingNumber: newRoutingNumber?.length > 9 ? newRoutingNumber.slice(0, 9) : newRoutingNumber,
        });
      } else {
        onChange(null);
      }
    },
    [onChange, account?.id],
  );

  // Only numerical values are allowed through the use of regular expressions!
  // We don't use type='number' because that allows negatives and 'e'
  const onlyNumbers = (value) => /^\d*$/.test(value);

  const handleRoutingNumberChange = (e) => {
    if (onlyNumbers(e.target.value)) {
      onChangeProxy(true, accountNumber, e.target.value);
    }
  };

  const handleOpenTerms = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up to the checkbox
    e.preventDefault(); // Prevent default behavior
    window.open('https://www.quicken.com/simplifi/terms', '_blank');
  };

  const handleAccountNumberChange = (e) => {
    if (onlyNumbers(e.target.value)) {
      onChangeProxy(true, e.target.value, routingNumber);
    }
  };

  const tooltipHTML = (
    <Typography component='span' variant='paragraphRegular'>
      <Typography component='span' variant='paragraphMedium'>
        Account number contain letters?
      </Typography>
      <br />
      {
        'Enter the numeric-only account number from the bottom of one of your checks, or consult your bank to find the correct account number for ACH transfers.'
      }
    </Typography>
  );

  return (
    <div className={classes.root}>
      <div className={cx(classes.grid, 'margin')}>
        <TextField
          className={classes.textField}
          value={routingNumber || ''}
          onChange={handleRoutingNumberChange}
          label='Routing number (9 digits)'
          variant='filled'
          inputProps={{ maxLength: 9 }}
          error={Boolean(fieldErrors?.routingNumber)}
          helperText={fieldErrors?.routingNumber || ' '}
        />
        <TextField
          className={classes.textField}
          value={accountNumber || ''}
          onChange={handleAccountNumberChange}
          label='Account number'
          variant='filled'
          error={Boolean(fieldErrors?.accountNumber)}
          helperText={fieldErrors?.accountNumber || ' '}
          InputProps={{
            maxLength: 17,
            endAdornment: <InfoTooltipIcon size='small' tooltipPlacement='right' tooltipHTML={tooltipHTML} />,
          }}
        />
        <Box marginTop={1} width={'100%'}>
          <FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  className={classes.checkbox}
                  onChange={() => {
                    if (!agreedToTerms) {
                      tracker.track(tracker.events.authorizeTransfer);
                    }
                    toggleAgreedToTerms();
                  }}
                />
              }
              label={
                <Typography className={classes.secondaryText} component='span' variant='paragraphRegular'>
                  I authorize Simplifi to initiate transfers to and from this account in line with &nbsp;
                  <Typography className={classes.termsLink} variant='inherit' onClick={handleOpenTerms}>
                    Simplifi’s Terms of Service
                    <OpenInNewIcon fontSize='inherit' />.
                  </Typography>
                </Typography>
              }
            />
          </FormControl>
        </Box>
      </div>
      <div className={classes.grid}>
        <div className={classes.checkWrapper}>
          <img src={theme.palette.mode === 'dark' ? CheckExampleGraphicDark : CheckExampleGraphic} alt='check' />
          <div className={classes.textRow}>
            <Typography component='span' variant={'captionRegular'} color='text.tertiary'>
              You can find info on the bottom of a check or on your&nbsp;
              {institution?.url ? (
                <Link
                  className={classes.linkItem}
                  href={institution?.url}
                  target='_blank'
                  underline='hover'
                  variant='captionRegular'
                  color='primary'
                >
                  {"institution's website"}
                </Link>
              ) : (
                "institution's website"
              )}
              .
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};
SelectAccountForm.propTypes = {
  account: PropTypes.object.isRequired,
  accountNumber: PropTypes.string,
  routingNumber: PropTypes.string,
  onChange: PropTypes.func,
  fieldErrors: PropTypes.object,
  institution: PropTypes.object,
  agreedToTerms: PropTypes.bool,
  toggleAgreedToTerms: PropTypes.func,
};

export default SelectAccountForm;
